import type { SubjectLeftAction } from '@Application/Subjects/Actions/subjectLeft';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { DocumentChangedAction } from '@Application/Beneficiaries/Documents/Actions/documentChanged';
import type { DocumentRemovedAction } from '@Application/Beneficiaries/Documents/Actions/documentRemoved';
import type { DocumentFetchFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentFetchFailed';
import type { DocumentFetchedAction } from '@Application/Beneficiaries/Documents/Actions/documentFetched';
import type { DocumentsFetchFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentsFetchFailed';
import type { DocumentsFetchedAction } from '@Application/Beneficiaries/Documents/Actions/documentsFetched';
import type { DocumentDeletedAction } from '@Application/Beneficiaries/Documents/Actions/documentDeleted';
import type { DocumentDeletionFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentDeletionFailed';
import type { DocumentFieldConfirmationFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentFieldConfirmationFailed';
import type { DocumentFieldConfirmedAction } from '@Application/Beneficiaries/Documents/Actions/documentFieldConfirmed';
import type { DocumentFieldCorrectedAction } from '@Application/Beneficiaries/Documents/Actions/documentFieldCorrected';
import type { DocumentFieldCorrectionFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentFieldCorrectionFailed';
import type { DocumentValidatedAction } from '@Application/Beneficiaries/Documents/Actions/documentValidated';
import type { DocumentValidationFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentValidationFailed';
import type { DocumentListItemAddedAction } from '@Application/Beneficiaries/Documents/Actions/documentListItemAdded';
import type { DocumentListItemAdditionFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentListItemAdditionFailed';
import type { DocumentListItemRemovedAction } from '@Application/Beneficiaries/Documents/Actions/documentListItemRemoved';
import type { DocumentListItemRemovalFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentListItemRemovalFailed';
import type { DocumentListItemMovedAction } from '@Application/Beneficiaries/Documents/Actions/documentListItemMoved';
import type { DocumentListItemMoveFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentListItemMoveFailed';
import DocumentDetailView from '@UI/Store/Documents/Views/DocumentDetailView';
import DocumentView from '@UI/Store/Documents/Views/DocumentView';
import DocumentsListView from '@UI/Store/Documents/Views/DocumentsListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type DocumentsStore = {
	documents: DocumentsListView | null;
	document: DocumentDetailView | null;
};

const initialState: DocumentsStore = {
	documents: null,
	document: null,
};

type Actions =
	| DocumentsFetchedAction
	// Already carried by `useStoreQuery`'s own `error` field: duplicating it here is the exact trap
	// `guides/state-stores.md` names.
	| DocumentsFetchFailedAction
	| DocumentFetchedAction
	| DocumentFetchFailedAction
	| DocumentChangedAction
	| DocumentRemovedAction
	// The verification gestures and the list item edits each answer the document entire: one handler replaces it and
	// replaces its row in the documents summary.
	| DocumentFieldCorrectedAction
	| DocumentFieldCorrectionFailedAction
	| DocumentFieldConfirmedAction
	| DocumentFieldConfirmationFailedAction
	| DocumentValidatedAction
	| DocumentValidationFailedAction
	| DocumentListItemAddedAction
	| DocumentListItemAdditionFailedAction
	| DocumentListItemRemovedAction
	| DocumentListItemRemovalFailedAction
	| DocumentListItemMovedAction
	| DocumentListItemMoveFailedAction
	| DocumentDeletedAction
	| DocumentDeletionFailedAction
	| SubjectLeftAction
	| UserRevokedAction;

const whenDocumentsFetched = (store: DocumentsStore, action: DocumentsFetchedAction): DocumentsStore => ({
	...store,
	documents: DocumentsListView.fromPayload(action.payload, action.typeKey, action.subjectId),
});

const whenDocumentFetched = (store: DocumentsStore, action: DocumentFetchedAction): DocumentsStore => ({
	...store,
	document: new DocumentDetailView(action.payload),
});

type DocumentReplacedAction =
	| DocumentFieldCorrectedAction
	| DocumentFieldConfirmedAction
	| DocumentValidatedAction
	| DocumentListItemAddedAction
	| DocumentListItemRemovedAction
	| DocumentListItemMovedAction;

// Correcting or confirming a field, validating the document and editing a list item each answer the document entire:
// one handler replaces it, and swaps its row in `documents` so the summary shows the fresh progress
// without reloading the list. A `null` list stays `null`.
const whenDocumentReplaced = (store: DocumentsStore, action: DocumentReplacedAction): DocumentsStore => ({
	...store,
	document: new DocumentDetailView(action.payload),
	documents: store.documents === null ? null : store.documents.replace(new DocumentView(action.payload)),
});

// The deleted document leaves the list, and the detail is cleared only when it is that very document.
const whenDocumentDeleted = (store: DocumentsStore, action: DocumentDeletedAction): DocumentsStore => ({
	...store,
	documents: store.documents === null ? null : store.documents.remove(action.payload.documentId),
	document: store.document?.id === action.payload.documentId ? null : store.document,
});

// The event stream pushes the document as the `GET` answers it. The list of its dossier gets the row
// replaced, or added first when it is unknown (the server creates generated documents on its own); the
// open detail is replaced only when it is that very document. A list that is absent or belongs to
// another dossier stays as it is.
const whenDocumentStreamed = (store: DocumentsStore, action: DocumentChangedAction): DocumentsStore => {
	const { documents, document } = store;
	const isListOfStream = documents?.isFor(new SubjectAddress(action.typeKey, action.subjectId)) === true;

	return {
		...store,
		documents: isListOfStream ? documents.upsert(new DocumentView(action.payload)) : documents,
		document: document?.id === action.payload.id ? new DocumentDetailView(action.payload) : document,
	};
};

const whenDocumentRemoved = (store: DocumentsStore, action: DocumentRemovedAction): DocumentsStore => {
	const { documents, document } = store;
	const { id } = action.payload;
	const isListOfStream = documents?.isFor(new SubjectAddress(action.typeKey, action.subjectId)) === true;

	return {
		...store,
		documents: isListOfStream ? documents.remove(id) : documents,
		document: document?.id === id ? null : document,
	};
};

const whenSubjectLeft = (store: DocumentsStore, action: SubjectLeftAction): DocumentsStore => ({
	...store,
	documents: store.documents?.isFor(new SubjectAddress(action.payload.typeKey, action.payload.subjectId))
		? null
		: store.documents,
	document: null,
});

const whenUserRevoked = (): DocumentsStore => initialState;

// `createSliceReducer`'s handler map is exhaustive over `Actions`: every failure still needs an entry,
// even though none of them changes this store's state — the error is already carried by
// `useStoreQuery`'s own `error` field.
const identity = (store: DocumentsStore): DocumentsStore => store;

export default registerStore<'documentsStore', Actions>(
	'documentsStore',
	createSliceReducer({
		DOCUMENTS_FETCHED: whenDocumentsFetched,
		DOCUMENTS_FETCH_FAILED: identity,
		DOCUMENT_FETCHED: whenDocumentFetched,
		DOCUMENT_FETCH_FAILED: identity,
		DOCUMENT_CHANGED: whenDocumentStreamed,
		DOCUMENT_REMOVED: whenDocumentRemoved,
		DOCUMENT_FIELD_CORRECTED: whenDocumentReplaced,
		DOCUMENT_FIELD_CORRECTION_FAILED: identity,
		DOCUMENT_FIELD_CONFIRMED: whenDocumentReplaced,
		DOCUMENT_FIELD_CONFIRMATION_FAILED: identity,
		DOCUMENT_VALIDATED: whenDocumentReplaced,
		DOCUMENT_VALIDATION_FAILED: identity,
		DOCUMENT_LIST_ITEM_ADDED: whenDocumentReplaced,
		DOCUMENT_LIST_ITEM_ADDITION_FAILED: identity,
		DOCUMENT_LIST_ITEM_REMOVED: whenDocumentReplaced,
		DOCUMENT_LIST_ITEM_REMOVAL_FAILED: identity,
		DOCUMENT_LIST_ITEM_MOVED: whenDocumentReplaced,
		DOCUMENT_LIST_ITEM_MOVE_FAILED: identity,
		DOCUMENT_DELETED: whenDocumentDeleted,
		DOCUMENT_DELETION_FAILED: identity,
		SUBJECT_LEFT: whenSubjectLeft,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
